import React, { useMemo } from 'react';
import { CalendarDays, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { User } from '../../types';
import { isPlaceholderName } from '../../utils/hierarchy';

/**
 * Branch tick-boxes and a month, for an agent who works more than one branch.
 *
 * Two things this fixes:
 *
 *  - An agent holding four branches had no way to look at one of them. Every
 *    list showed all four at once.
 *  - Month-based screens (collections, commission, visits) each had their own
 *    idea of which month they were showing, and none of them defaulted to the
 *    real current month -- a hard-coded month string had been left in from
 *    demo data, so August's figures were shown in September.
 *
 * The branch row is hidden entirely for an agent with one branch, because a
 * tick box with one option is noise.
 */

export function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonth(monthKey: string, delta: number): string {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  if (!y || !m) return monthKey;
  const d = new Date(y, m - 1, 1);
  const label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  return monthKey === currentMonthKey() ? `${label} (this month)` : label;
}

/**
 * The key two spellings of one branch share.
 *
 * Capitals and spacing only, because that is all that differs between
 * "GARKHEDA" and "Garkheda", or "MILL CORNER" and "Mill  Corner". Anything
 * more aggressive would start merging branches that really are different.
 */
export function branchKey(name?: string): string {
  return (name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** True when two branch names are the same branch, however they are typed. */
export function sameBranch(a?: string, b?: string): boolean {
  const ka = branchKey(a);
  return !!ka && ka === branchKey(b);
}

export interface AgentScopeBarProps {
  user: User;
  /** Ticked branches. An empty list means every branch this agent holds. */
  selectedBranches: string[];
  onBranchesChange: (next: string[]) => void;
  month: string;
  onMonthChange: (next: string) => void;
  /**
   * Branch names as the branch records hold them. When a branch is spelt more
   * than one way, this is the spelling shown, because it is the one an
   * administrator chose deliberately rather than whatever arrived in a
   * spreadsheet.
   */
  branchRecordNames?: string[];
}

export const AgentScopeBar: React.FC<AgentScopeBarProps> = ({
  user,
  selectedBranches,
  onBranchesChange,
  month,
  onMonthChange,
  branchRecordNames,
}) => {
  /**
   * ONE ENTRY PER REAL BRANCH.
   *
   * This list was built with a plain Set, which treats "GARKHEDA" and
   * "Garkheda" as two different branches, so the same branch appeared twice in
   * the tick-list and ticking one of them showed only the accounts filed under
   * that exact spelling. Names are now folded on capitals and spacing, and one
   * spelling survives: the branch record's when there is one, otherwise the
   * first that came in.
   */
  const myBranches = useMemo(() => {
    const preferred = (branchRecordNames || []).filter(
      (b): b is string => !!b && !isPlaceholderName(b)
    );
    const mine = (user.branches && user.branches.length ? user.branches : [user.branch])
      .filter((b): b is string => !!b && !isPlaceholderName(b));

    // Only branches this agent actually holds are offered; the records are
    // consulted for the spelling alone.
    const held = new Set(mine.map(branchKey));
    const chosen = new Map<string, string>();
    preferred.forEach((n) => {
      const k = branchKey(n);
      if (k && held.has(k) && !chosen.has(k)) chosen.set(k, n.trim());
    });
    mine.forEach((n) => {
      const k = branchKey(n);
      if (k && !chosen.has(k)) chosen.set(k, n.trim());
    });

    return Array.from(chosen.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [user, branchRecordNames]);

  const showBranches = myBranches.length > 1;
  const thisMonth = currentMonthKey();

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 bg-white border border-slate-200 rounded-2xl text-xs shadow-xs">
      {showBranches && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 text-slate-500 font-bold shrink-0">
            <Building2 className="w-3.5 h-3.5" />
            <span>Branches</span>
          </span>
          <button
            type="button"
            onClick={() => onBranchesChange([])}
            className={`px-2.5 py-1.5 rounded-lg font-bold border transition ${
              selectedBranches.length === 0
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            All ({myBranches.length})
          </button>
          {myBranches.map((b) => {
            // Ticked state is matched on the folded name too, so a selection
            // saved under another spelling still shows as ticked.
            const on = selectedBranches.some((x) => sameBranch(x, b));
            return (
              <label
                key={`asb-${b}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold border cursor-pointer transition ${
                  on
                    ? 'bg-blue-50 text-blue-800 border-blue-200'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) =>
                    onBranchesChange(
                      e.target.checked
                        ? [...selectedBranches.filter((x) => !sameBranch(x, b)), b]
                        : selectedBranches.filter((x) => !sameBranch(x, b))
                    )
                  }
                />
                <span>{b}</span>
              </label>
            );
          })}
        </div>
      )}

      <div className="flex-1" />

      <div className="flex items-center gap-1.5 shrink-0">
        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
        <button
          type="button"
          onClick={() => onMonthChange(shiftMonth(month, -1))}
          className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition"
          title="Previous month"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-900 min-w-[150px] text-center">
          {monthLabel(month)}
        </span>
        <button
          type="button"
          onClick={() => onMonthChange(shiftMonth(month, 1))}
          disabled={month >= thisMonth}
          className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          title="Next month"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        {month !== thisMonth && (
          <button
            type="button"
            onClick={() => onMonthChange(thisMonth)}
            className="px-2.5 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg font-bold hover:bg-blue-100 transition"
          >
            This month
          </button>
        )}
      </div>
    </div>
  );
};

export default AgentScopeBar;
